import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Circle } from "lucide-react";
import { ExtraSheetView, SheetView } from "@/components/SheetView";
import { SignBlock, SignedBox } from "@/components/SignBlock";

function defaultName(role, data) {
  if (role === "gestore") return "Primerano Francesco";
  if (role.startsWith("cliente2")) return data.cliente2_nome || "";
  return data.cliente_nome || "";
}

export function SigningFlow({ pending, signatures, data, extras, template, onSign }) {
  const [sel, setSel] = useState(null);
  const [lastImages, setLastImages] = useState({});
  const advance = useRef(false);
  const total = pending.reduce((t, i) => t + i.roles.length, 0);
  const done = pending.reduce((t, i) => t + i.signed.length, 0);

  useEffect(() => {
    const cur = pending.find((i) => i.page_key === sel);
    if (!cur || (advance.current && cur.missing.length === 0)) {
      advance.current = false;
      const next = pending.find((i) => i.missing.length) || cur || pending[0];
      setSel(next?.page_key || null);
      if (next && next.page_key !== sel) window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [pending, sel]);

  const item = pending.find((i) => i.page_key === sel);
  const page = useMemo(() => {
    if (!item) return null;
    if (item.kind === "extra") return { extra: extras.find((e) => e.id === item.extra_id) };
    return { page: template.pages.find((p) => p.key === item.page_key) };
  }, [item, extras, template]);

  const sign = async (body) => {
    await onSign(body);
    advance.current = true;
  };
  const remember = (role, img) => setLastImages((m) => ({ ...m, [role.replace("_spec", "")]: img }));
  const sigFor = (role) => signatures.find((s) => s.page_key === sel && s.role === role);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <aside className="lg:col-span-3 lg:sticky lg:top-24 rounded-xl border border-[#D4E6EC] bg-white p-4" data-testid="signing-sheet-list">
        <div className="text-xs uppercase tracking-widest text-[#7B929C]">Avanzamento firme</div>
        <div className="font-serif-it text-3xl font-semibold mt-1" data-testid="signing-progress">{done} / {total}</div>
        <div className="h-1.5 rounded-full bg-[#EAF5F7] mt-2 overflow-hidden"><div className="h-full bg-[#2A9D8F] transition-all" style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></div>
        <ul className="mt-4 space-y-1">
          {pending.map((i, idx) => (
            <li key={i.page_key}>
              <button data-testid={`signing-sheet-${i.page_key}`} onClick={() => setSel(i.page_key)}
                className={`w-full flex items-center gap-2 text-left text-sm px-3 py-2 rounded-lg transition-colors ${sel === i.page_key ? "bg-[#0B6E8F] text-white" : "hover:bg-[#EAF5F7]"}`}>
                {i.missing.length ? <Circle size={15} /> : <CheckCircle2 size={15} className={sel === i.page_key ? "" : "text-[#2A9D8F]"} />}
                <span className="flex-1">Foglio {idx + 1} • {i.label}</span>
                <span className="text-[11px] opacity-80">{i.signed.length}/{i.roles.length}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <div className="lg:col-span-9 space-y-5">
        {page?.page && <SheetView page={page.page} data={data} template={template} />}
        {page?.extra && <ExtraSheetView extra={page.extra} data={data} template={template} />}
        {item && (
          <div className="space-y-4" key={item.page_key}>
            <h3 className="font-serif-it text-2xl font-semibold">Firme richieste su questo foglio</h3>
            {item.roles.map((role) => {
              const s = sigFor(role);
              const label = template.role_labels[role] || role;
              return s ? <SignedBox key={role} sig={s} label={label} /> : (
                <SignBlock key={role} pageKey={item.page_key} role={role} label={label} defaultName={defaultName(role, data)}
                  needPrivacy={item.page_key === "p1" && role !== "gestore"} onSign={sign} lastImage={lastImages[role.replace("_spec", "")]} onDrawn={remember} />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
